import { useParams } from 'react-router-dom';
import { Box } from '@theme-ui/components';

import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';

import { useProductDetailsFromUrlQuery } from './__graphql__/WhatsNewForMe';
import Content from './Content';

const INCLUDED_PRODUCTS_LINK_TYPE = 'included-products';

const WhatsNewForMe = () => {
  const { url_key } = useParams<{
    url_key: string;
  }>();

  const { getLanguagesWithFallback, findTranslationByKeyWithFallback } =
    useLocaleWithFallback();

  const {
    data: productDetailsData,
    loading: productDetailsDataLoading,
    error: productDetailsError,
  } = useProductDetailsFromUrlQuery({
    variables: { url_key, languages: getLanguagesWithFallback() },
  });

  if (productDetailsDataLoading) return null;

  if (productDetailsError) {
    throw new Error(
      `Unable to get product details from url_key ${url_key}: ${productDetailsError}`,
    );
  }

  const layoutItem =
    productDetailsData?.content?.pricing_pages?.[0]?.layout?.[0]?.item;

  // For What's new page we just need single layout items
  const isSingleLayout =
    layoutItem?.__typename === 'pricing_pages_single_layout';

  if (!isSingleLayout) {
    throw new Error(`What's new page is only valid for Single Layout pages`);
  }

  const sku = isSingleLayout ? layoutItem?.product?.sku : '';
  const title = isSingleLayout ? layoutItem?.product?.title : '';
  const links = isSingleLayout ? layoutItem?.product?.links : [];

  const headerLinks = links?.map((link) => {
    const translations = link?.products_links_id?.translations;
    return {
      url: findTranslationByKeyWithFallback({
        translations,
        key: 'url',
      }),
      link_type: link?.products_links_id?.link_type,
    };
  });

  const includedProductsUrl = headerLinks?.filter(
    (link) => link?.link_type === INCLUDED_PRODUCTS_LINK_TYPE,
  )?.[0]?.url;

  return (
    <Box data-testid="whats-new-for-me-page">
      {sku && title && (
        <Content
          sku={sku}
          title={title}
          includedProductsUrl={includedProductsUrl || ''}
        />
      )}
    </Box>
  );
};

export default WhatsNewForMe;
